/**
 * Constants for GlobalMenuModal (copy and validation limits).
 * Display name limits match server displayName.helper.js (3–20 chars).
 */

import type { InAppLegalDocumentVariant } from '../../src/landing/types/legalDocument.types'
import { ANALYTICS_MENU_ITEMS } from '../../src/analytics/analytics.constants'
import { getWcBrand } from '../../src/worldcup/helpers/wcBrand.helper'

/** Analytics `menu_item` for each legal doc opened from the Privacy submenu modal. */
export const GLOBAL_MENU_PRIVACY_SUBMENU_LEGAL_ANALYTICS: Record<
  InAppLegalDocumentVariant,
  (typeof ANALYTICS_MENU_ITEMS)[
    | 'PRIVACY_POLICY_MODAL'
    | 'PRIVACY_DATA_DELETION_MODAL'
    | 'PRIVACY_TERMS_MODAL'
    | 'PRIVACY_CREDITS_MODAL'
  ]
> = {
  privacy: ANALYTICS_MENU_ITEMS.PRIVACY_POLICY_MODAL,
  dataDeletion: ANALYTICS_MENU_ITEMS.PRIVACY_DATA_DELETION_MODAL,
  terms: ANALYTICS_MENU_ITEMS.PRIVACY_TERMS_MODAL,
  credits: ANALYTICS_MENU_ITEMS.PRIVACY_CREDITS_MODAL,
}

/** Drawer panel (RN Modal portal); used by Storybook play and e2e. */
export const GLOBAL_MENU_MODAL_CONTENT_TEST_ID = 'global-menu-modal-content';

/** Storybook wrapper only: control that opens the menu (mirrors real hamburger flow). */
export const GLOBAL_MENU_MODAL_STORY_OPEN_MENU_TEST_ID = 'global-menu-modal-story-open-menu';

export const DISPLAY_NAME_MIN_LENGTH = 3;
export const DISPLAY_NAME_MAX_LENGTH = 20;

/**
 * Top-level React Query roots whose cached payloads embed OTHER users' display names.
 * After a display-name save we invalidate these so the editor's own name refreshes across
 * every surface (friends, groups, challenge cards, stats, suggestions, predictions) rather
 * than only the World Cup leaderboard. Other viewers refresh on their next natural refetch.
 */
export const DISPLAY_NAME_BEARING_QUERY_ROOTS: readonly string[] = [
  'friends',
  'friendStats',
  'friendSuggestions',
  'groups',
  'challenges',
  'predictions',
];

/** Menu label: opens the tournament rules modal (predictions). */
export const getGlobalMenuWorldCupRulesLabel = (): string => `${getWcBrand().NAME} rules`;

/** Menu label: opens the layered avatar customization modal. */
export const GLOBAL_MENU_AVATAR_LOOK_PREVIEW_LABEL = 'Customize avatar';

/** Menu label: Privacy and data (legal + account deletion). */
export const GLOBAL_MENU_PRIVACY_AND_DATA_LABEL = 'Privacy and data';

/** Menu label: open marketing site (web: in-app /; native: browser → production origin). */
export const GLOBAL_MENU_OFFICIAL_WEBSITE_LABEL = 'Zporti website';

/** Menu row: mute game sound effects (SFX). */
export const GLOBAL_MENU_SOUND_EFFECTS_LABEL = 'Game sounds';

/** Menu label: promo coupon → coins. */
export const GLOBAL_MENU_REDEEM_COUPON_LABEL = 'Redeem coupon'

/** Menu label: contact support. */
export const GLOBAL_MENU_SUPPORT_LABEL = 'Support';

/** Active read-only impersonation: profile strip in GlobalMenuModal. */
export const GLOBAL_MENU_IMPERSONATION = {
  VIEWING_AS_PREFIX: 'Viewing as',
  EXIT_IMPERSONATION: 'Exit impersonation',
} as const

/**
 * Guest-account upgrade strip in the profile menu — the always-available path to convert.
 * Mirrors the impersonation strip's "you're currently on X, act here" pattern. Honest carrot only.
 */
export const GLOBAL_MENU_GUEST_UPGRADE = {
  STATUS: "You're on a guest account. Sign up to save it.",
  CTA: 'Create account',
  /** Analytics `action` label for this surface. */
  ACTION: 'menu',
} as const

export const GLOBAL_MENU_COPY = {
  FULL_NAME_LABEL: 'Display name',
  SAVE: 'Save',
  CANCEL: 'Cancel',
  DISPLAY_NAME_HINT: `${DISPLAY_NAME_MIN_LENGTH}–${DISPLAY_NAME_MAX_LENGTH} characters`,
  DISPLAY_NAME_TOO_SHORT: `Enter at least ${DISPLAY_NAME_MIN_LENGTH} characters`,
  DISPLAY_NAME_TOO_LONG: `Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or less`,
  DISPLAY_NAME_UPDATE_FAILED: 'Could not update display name. Try again.',
  DISPLAY_NAME_NOT_ALLOWED: 'This name is not allowed. Please choose another.',
  SOUND_EFFECTS_ON: 'On',
  SOUND_EFFECTS_MUTED: 'Muted',
} as const;
